import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { KrzzLogo } from "./KrzzLogo";

// Flag components for clean display
const FlagRO = () => (
  <svg className="w-5 h-4 rounded-sm overflow-hidden shadow-sm" viewBox="0 0 30 20">
    <rect width="10" height="20" fill="#002B7F"/>
    <rect x="10" width="10" height="20" fill="#FCD116"/>
    <rect x="20" width="10" height="20" fill="#CE1126"/>
  </svg>
);

const FlagGB = () => (
  <svg className="w-5 h-4 rounded-sm overflow-hidden shadow-sm" viewBox="0 0 60 30">
    <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
    <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
    <g clipPath="url(#s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "acasa", label: "Acasă", isSection: true },
    { href: "masini", label: "Stoc", isSection: true },
    { href: "contact", label: "Contact", isSection: true },
  ];

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (sectionId === "acasa") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100);
    } else {
      if (sectionId === "acasa") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? "bg-[#0b0b0b]/95 backdrop-blur-xl border-b border-[#2a2a2a]"
        : "bg-transparent"
      }`}>
      <div className="container-auto">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex items-center shrink-0 cursor-pointer"
            data-testid="header-logo"
          >
            <KrzzLogo className="text-white scale-150" size="large" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="px-5 py-2 font-medium text-sm transition-all text-gray-300 hover:text-white"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href="tel:0746184905"
              className="flex items-center gap-2 font-semibold transition-colors text-white"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">0746 184 905</span>
            </a>
            <div className="flex items-center gap-1.5 pl-3 border-l border-[#2a2a2a]">
              <FlagRO />
              <FlagGB />
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-12 h-12 rounded-lg flex items-center justify-center transition-colors text-white hover:bg-[#1a1a1a]"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-[#111111] border-b border-[#2a2a2a] animate-fade-in">
            <nav className="container-auto py-6">
              <div className="space-y-1">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="w-full text-left px-4 py-4 font-medium text-gray-300 hover:text-white hover:bg-[#1a1a1a] rounded-lg transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-[#2a2a2a]">
                <a
                  href="tel:0746184905"
                  className="flex items-center justify-between px-4 py-4 text-white font-semibold hover:bg-[#1a1a1a] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5" />
                    0746 184 905
                  </div>
                  <div className="flex items-center gap-1.5">
                    <FlagRO />
                    <FlagGB />
                  </div>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
